import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MenuModule, Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  TableColumn,
  SortEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  PurchaseRequestService,
  PurchaseRequestSearchParams,
} from './purchase-request.service';
import {
  PURCHASE_REQUEST_DEFAULT_CONFIG,
  PURCHASE_REQUEST_SEARCH_CONFIG,
  PURCHASE_REQUEST_TABLE_COLUMNS,
  PURCHASE_REQUEST_DEFAULT_COLUMNS_ORDER,
  PURCHASE_REQUEST_FLEXIBLE_COLUMNS_ORDER,
  PURCHASE_REQUEST_ACTION_COLUMN,
  PurchaseRequestRecord,
  PurchaseRequestStatus,
  PurchaseRequestPurpose,
  PURCHASE_REQUEST_STATUS_SEVERITY,
  PURCHASE_REQUEST_PURPOSE_SEVERITY,
  PURCHASE_REQUEST_STATUS_OPTIONS,
  PURCHASE_REQUEST_PURPOSE_OPTIONS,
} from './purchase-request.config';
import { StatusLabelPipe, StatusStylePipe } from './pipes/status.pipe';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { PermissionService } from '@app/core/services/permission.service';
import { P_PROCUREMENT } from '@app/core/constants/permissions';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';

// Action interface for purchase request actions
interface PurchaseRequestAction {
  action: string;
  label: string;
  icon: string;
  severity: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
  text: boolean;
  disabled?: boolean;
  visible?: boolean;
}

@Component({
  selector: 'app-purchase-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    MenuModule,
    ToastModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
    StatusLabelPipe,
    StatusStylePipe,
  ],
  providers: [MessageService],
  templateUrl: './purchase-request.component.html',
  styleUrl: './purchase-request.component.css',
})
export class PurchaseRequestComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly translateService = inject(TranslateService);
  private readonly dateFormatterService = inject(DateFormatterService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'purchase_request' for procurement permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_request.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_request.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_request.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_request.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_request.approve');
  });

  // ViewChild references
  @ViewChild('actionMenu') actionMenu!: Menu;

  // Search form configuration
  searchFormat = signal<FormFieldConfig[]>([]);

  // Check if user has any action permissions (READ, UPDATE, DELETE, or APPROVE)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      PURCHASE_REQUEST_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of PURCHASE_REQUEST_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(PURCHASE_REQUEST_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      PURCHASE_REQUEST_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of PURCHASE_REQUEST_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Data signals
  dataSource = signal<PurchaseRequestRecord[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  rows = signal(PURCHASE_REQUEST_DEFAULT_CONFIG.PAGE_SIZE);

  // Search parameters (using 1-based pagination like brand component)
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: PURCHASE_REQUEST_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Sorting signals
  sortField = signal<string>('');
  sortOrder = signal<1 | -1>(-1);

  // Dialog signals
  selectedPurchaseRequest = signal<PurchaseRequestRecord | null>(null);
  showDeleteDialog = signal(false);
  showApproveDialog = signal(false);
  showRejectDialog = signal(false);
  showStatusChangeDialog = signal(false);
  selectedNewStatus = signal<string | null>(null);

  // Menu signals
  currentMenuModel = signal<any[]>([]);
  currentMenuRecord = signal<PurchaseRequestRecord | null>(null);

  // Configuration constants (for template access)
  readonly PURCHASE_REQUEST_DEFAULT_CONFIG = PURCHASE_REQUEST_DEFAULT_CONFIG;
  readonly PurchaseRequestStatus = PurchaseRequestStatus;
  readonly PurchaseRequestPurpose = PurchaseRequestPurpose;

  // Primary button configuration base
  private readonly primaryButtonBase = {
    label: 'common.actions.new',
    icon: 'add',
    severity: 'info' as const,
    show: true,
  };

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Dialog button configurations
  deleteDialogButtons: ConfirmationButton[] = [
    {
      label: 'purchase_request.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'purchase_request.dialog.actions.delete',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  approveDialogButtons: ConfirmationButton[] = [
    {
      label: 'purchase_request.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'purchase_request.dialog.actions.approve',
      action: 'confirm',
      severity: 'success',
    },
  ];

  rejectDialogButtons: ConfirmationButton[] = [
    {
      label: 'purchase_request.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'purchase_request.dialog.actions.reject',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  statusChangeDialogButtons: ConfirmationButton[] = [
    {
      label: 'purchase_request.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'purchase_request.dialog.actions.change',
      action: 'confirm',
      severity: 'info',
    },
  ];

  ngOnInit(): void {
    this.initializeConfigurations();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), ...searchData, page: 1 });
    this.loadData(searchData);
  }

  onReset(): void {
    this.searchParams.set({
      page: 1,
      size: PURCHASE_REQUEST_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.loadData();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadData();
  }

  onSortChange(sortEvent: SortEvent): void {
    this.sortField.set(sortEvent.field);
    this.sortOrder.set(sortEvent.order);
    this.loadData();
  }

  onViewDetails(record: PurchaseRequestRecord): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-request/view', record.id], {
      queryParams: { title: record.title },
    });
  }

  onEditPurchaseRequest(record: PurchaseRequestRecord): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-request/edit', record.id], {
      queryParams: { title: record.title },
    });
  }

  onChangeStatus(record: PurchaseRequestRecord): void {
    this.selectedPurchaseRequest.set(record);
    this.selectedNewStatus.set(null);
    this.showStatusChangeDialog.set(true);
  }

  onDeletePurchaseRequest(record: PurchaseRequestRecord): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPurchaseRequest.set(record);
    this.showDeleteDialog.set(true);
  }

  onCreatePurchaseOrder(record: PurchaseRequestRecord): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-order/add'], {
      queryParams: { purchaseRequestId: record.id },
    });
  }

  onApprovePurchaseRequest(record: PurchaseRequestRecord): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPurchaseRequest.set(record);
    this.showApproveDialog.set(true);
  }

  onRejectPurchaseRequest(record: PurchaseRequestRecord): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPurchaseRequest.set(record);
    this.showRejectDialog.set(true);
  }

  onAddPurchaseRequest(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-request/add']);
  }

  onDeleteConfirm(action: string): void {
    if (action === 'confirm') {
      const purchaseRequest = this.selectedPurchaseRequest();
      if (purchaseRequest) {
        this.purchaseRequestService
          .deletePurchaseRequest(purchaseRequest.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'purchase_request.messages.deleted_successfully',
                ),
              });
              this.loadData();
              this.showDeleteDialog.set(false);
              this.selectedPurchaseRequest.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'purchase_request.messages.failed_to_delete',
                ),
              });
            },
          });
      }
    } else {
      this.showDeleteDialog.set(false);
      this.selectedPurchaseRequest.set(null);
    }
  }

  onApproveConfirm(action: string): void {
    if (action === 'confirm') {
      const purchaseRequest = this.selectedPurchaseRequest();
      if (purchaseRequest) {
        this.purchaseRequestService
          .updatePurchaseRequestStatus(purchaseRequest.id, 'APPROVED')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'purchase_request.messages.approved_successfully',
                ),
              });
              this.loadData();
              this.showApproveDialog.set(false);
              this.selectedPurchaseRequest.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'purchase_request.messages.failed_to_approve',
                ),
              });
            },
          });
      }
    } else {
      this.showApproveDialog.set(false);
      this.selectedPurchaseRequest.set(null);
    }
  }

  onRejectConfirm(action: string): void {
    if (action === 'confirm') {
      const purchaseRequest = this.selectedPurchaseRequest();
      if (purchaseRequest) {
        this.purchaseRequestService
          .updatePurchaseRequestStatus(purchaseRequest.id, 'REJECTED')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'purchase_request.messages.rejected_successfully',
                ),
              });
              this.loadData();
              this.showRejectDialog.set(false);
              this.selectedPurchaseRequest.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'purchase_request.messages.failed_to_reject',
                ),
              });
            },
          });
      }
    } else {
      this.showRejectDialog.set(false);
      this.selectedPurchaseRequest.set(null);
    }
  }

  onStatusChangeConfirm(action: string): void {
    if (action === 'confirm') {
      const purchaseRequest = this.selectedPurchaseRequest();
      const newStatus = this.selectedNewStatus();
      if (purchaseRequest && newStatus) {
        this.purchaseRequestService
          .updatePurchaseRequestStatus(purchaseRequest.id, newStatus as any)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'purchase_request.messages.status_updated_successfully',
                ),
              });
              this.loadData();
              this.showStatusChangeDialog.set(false);
              this.selectedPurchaseRequest.set(null);
              this.selectedNewStatus.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'purchase_request.messages.failed_to_update_status',
                ),
              });
            },
          });
      }
    } else {
      this.showStatusChangeDialog.set(false);
      this.selectedPurchaseRequest.set(null);
      this.selectedNewStatus.set(null);
    }
  }

  private loadData(searchParams?: SearchFormData): void {
    this.loading.set(true);

    const params: PurchaseRequestSearchParams = {
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
      size: this.searchParams().size,
      sortBy:
        this.sortField() || PURCHASE_REQUEST_DEFAULT_CONFIG.DEFAULT_SORT_FIELD,
      ascending: this.sortOrder() === 1,
      deleteFlag: false, // Only show non-deleted items
      ...searchParams,
    };

    this.purchaseRequestService
      .getPurchaseRequestRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.pageContent);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_load_data',
            ),
          });
        },
      });
  }

  private initializeConfigurations(): void {
    // Initialize search form configuration
    this.searchFormat.set(PURCHASE_REQUEST_SEARCH_CONFIG);

    // Columns are now computed based on permissions, no need to set them here

    // Load translated options
    this.loadTranslatedOptions();
  }

  private loadTranslatedOptions(): void {
    // Load purpose options with translations
    const translatedPurposeOptions = PURCHASE_REQUEST_PURPOSE_OPTIONS.map(
      (option: any) => ({
        ...option,
        label: this.translateService.instant(
          `purchase_request.purpose.${option.value.toLowerCase()}`,
        ),
      }),
    );

    // Load status options with translations
    const translatedStatusOptions = PURCHASE_REQUEST_STATUS_OPTIONS.map(
      (option: any) => ({
        ...option,
        label: this.translateService.instant(option.label),
      }),
    );

    // Update search form with translated options
    this.searchFormat.set(
      this.searchFormat().map((field) => {
        if (field.key === 'purpose') {
          return { ...field, options: translatedPurposeOptions };
        } else if (field.key === 'status') {
          return { ...field, options: translatedStatusOptions };
        }
        return field;
      }),
    );
  }

  getPurposeSeverity(
    purpose: string,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return PURCHASE_REQUEST_PURPOSE_SEVERITY[purpose] || 'secondary';
  }

  getPurposeLabel(purpose: string): string {
    if (!purpose) {
      return '';
    }
    return this.translateService.instant(
      `purchase_request.purpose.${purpose.toLowerCase()}`,
    );
  }

  formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  getNextPossibleStatuses(): { label: string; value: string }[] {
    // Return all status options for now - in a real app, this would be based on current status
    return PURCHASE_REQUEST_STATUS_OPTIONS.map((option) => ({
      label: this.translateService.instant(
        `purchase_request.status.${option.value.toLowerCase()}`,
      ),
      value: option.value,
    }));
  }

  /**
   * Format date using custom format (HH:MM DD/MM/YYYY)
   * @param dateInput - Date input in any format
   * @returns Formatted date string
   */
  formatDate(dateInput: any): string {
    if (!dateInput) return '-';

    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return '-';

      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${hours}:${minutes} ${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  }

  /**
   * Format schedule date to show only date (DD/MM/YYYY)
   * @param dateInput - Date input in any format
   * @returns Formatted date string without time
   */
  formatScheduleDate(dateInput: any): string {
    if (!dateInput) return '-';

    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return '-';

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  }

  /**
   * Get available actions for a purchase request record
   * @param record - Purchase request record
   * @returns Array of available actions with permission-based visibility
   */
  getPurchaseRequestActions(
    record: PurchaseRequestRecord,
  ): PurchaseRequestAction[] {
    const actions: PurchaseRequestAction[] = [];

    // View action - only include if user has read permission
    if (this.canRead()) {
      actions.push({
        action: 'view',
        label: this.translateService.instant('common.actions.view'),
        icon: 'pi pi-eye',
        severity: 'info',
        text: true,
        visible: true,
      });
    }

    // Add other actions based on status and permissions
    if (
      record.status !== 'APPROVED' &&
      record.status !== 'REJECTED' &&
      this.canUpdate()
    ) {
      actions.push({
        action: 'edit',
        label: this.translateService.instant('common.actions.edit'),
        icon: 'pi pi-pencil',
        severity: 'secondary',
        text: true,
        visible: true,
      });
    }

    if (
      (record.status === 'PENDING' || record.status === 'PENDING_APPROVAL') &&
      this.canApprove()
    ) {
      actions.push(
        {
          action: 'approve',
          label: this.translateService.instant(
            'purchase_request.actions.approve',
          ),
          icon: 'pi pi-check',
          severity: 'success',
          text: true,
          visible: true,
        },
        {
          action: 'reject',
          label: this.translateService.instant(
            'purchase_request.actions.reject',
          ),
          icon: 'pi pi-times',
          severity: 'danger',
          text: true,
          visible: true,
        },
      );
    }

    if (
      (record.status === 'ORDERED' || record.status === 'APPROVED') &&
      this.canCreate()
    ) {
      actions.push({
        action: 'create-purchase-order',
        label: this.translateService.instant(
          'purchase_request.actions.create_purchase_order',
        ),
        icon: 'add',
        severity: 'info',
        text: true,
        visible: true,
      });
    }

    if (
      (record.status !== 'APPROVED' || !record.activeOrder) &&
      this.canDelete()
    ) {
      actions.push({
        action: 'delete',
        label: this.translateService.instant('common.actions.delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        text: true,
        visible: true,
      });
    }

    return actions;
  }

  /**
   * Check if purchase request has any enabled actions (excluding view)
   * @param record - Purchase request record
   * @returns true if there are enabled actions
   */
  hasEnabledPurchaseRequestActions(record: PurchaseRequestRecord): boolean {
    const actions = this.getPurchaseRequestActions(record);
    return actions.some((action) => {
      if (action.action === 'view') return false; // View is handled separately
      return action.visible !== false;
    });
  }

  /**
   * Get actions model for PrimeNG menu
   * @param record - Purchase request record
   * @returns Menu items array
   */
  getActionsModel(record?: PurchaseRequestRecord): any[] {
    const targetRecord = record || this.currentMenuRecord();

    if (!targetRecord) {
      return [];
    }

    const actions = this.getPurchaseRequestActions(targetRecord);

    // Filter out disabled actions and view action (view is shown separately)
    const menuItems = actions
      .filter(
        (action) =>
          action.action !== 'view' &&
          action.visible !== false &&
          !action.disabled,
      )
      .map((action) => ({
        label: action.label,
        command: () => {
          this.onActionClick(action.action);
        },
      }));

    return menuItems;
  }

  /**
   * Handle action button clicks
   * @param action - Action type
   * @param record - Purchase request record
   */
  onActionClick(action: string, record?: PurchaseRequestRecord): void {
    const targetRecord = record || this.currentMenuRecord();

    if (!targetRecord) {
      console.warn('No record available for action:', action);
      return;
    }

    switch (action) {
      case 'view':
        this.onViewDetails(targetRecord);
        break;
      case 'edit':
        this.onEditPurchaseRequest(targetRecord);
        break;
      case 'approve':
        this.onApprovePurchaseRequest(targetRecord);
        break;
      case 'reject':
        this.onRejectPurchaseRequest(targetRecord);
        break;
      case 'create-purchase-order':
        this.onCreatePurchaseOrder(targetRecord);
        break;
      case 'delete':
        this.onDeletePurchaseRequest(targetRecord);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  }

  /**
   * Toggle menu for Others actions
   * @param event - Click event
   * @param record - Purchase request record
   */
  toggleMenu(event: Event, record: PurchaseRequestRecord): void {
    event.preventDefault();
    event.stopPropagation();

    // Set the current record and menu model
    this.currentMenuRecord.set(record);
    this.currentMenuModel.set(this.getActionsModel(record));

    // Toggle the menu using ViewChild reference
    if (this.actionMenu) {
      this.actionMenu.toggle(event);
    }
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }
}
